import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useNavigate, useLocation } from 'react-router-dom';

const getColorClass = (value) => {
  if (value > 0.7) return 'bg-green-500';
  if (value > 0.6) return 'bg-lime-300';
  if (value > 0.5) return 'bg-yellow-500';
  if (value > 0.4) return 'bg-orange-500';
  return 'bg-red-500';
};



const PerformanceCell = (params) => (
  <Tippy content={`${params.colDef.headerName}: ${params.value.toFixed(3)}`}>
    <div className="flex items-center justify-center w-full h-full">
      <div className="bg-gray-200 w-2/5 h-2 rounded-full">
        <div 
          className={`${getColorClass(params.value)} h-full rounded-full`} 
          style={{width: `${params.value * 100}%`}}
        ></div>
      </div>
    </div>
  </Tippy>
);


const getSentimentColorClassAndLabel = (value) => {
  if (value > 0.5) return { colorClass: 'bg-green-500', label: 'VERY POSITIVE' };
  if (value > 0.25) return { colorClass: 'bg-green-300', label: 'POSITIVE' };
  if (value > -0.25) return { colorClass: 'bg-yellow-300', label: 'NEUTRAL' };
  if (value > -0.5) return { colorClass: 'bg-orange-500', label: 'NEGATIVE' };
  return { colorClass: 'bg-red-700', label: 'VERY NEGATIVE' };
};

const SentimentCell = (params) => {
  const { colorClass, label } = getSentimentColorClassAndLabel(params.value);

  return (
    <Tippy content={`${label}: ${params.value.toFixed(3)}`}>
      <div className="flex items-center justify-center w-full h-full">
        <div className="bg-gray-200 w-2/5 h-2 rounded-full">
          <div 
            className={`${colorClass} h-full rounded-full`} 
            style={{width: `${Math.abs(params.value) * 100}%`}} // Ensure the width is positive
          ></div>
        </div>
      </div>
    </Tippy>
  );
};


const AgentsGrid = ({ allAgentInfo, filters }) => {
  const [gridApi, setGridApi] = useState(null);
  const [rowData, setRowData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  
  const handleAgentClick = (agent) => {
    navigate(`/agent/${encodeURIComponent(agent.agent)}`, { state: { referrer: "/agents", filters, agent } });
  };

  const AgentLinkCell = (params) => (
    <div className="text-right">
      <button
        onClick={() => handleAgentClick(params.data)}
        className="text-blue-600 hover:underline"
      >
        {params.value}
      </button>
    </div>
  );

  const columns = [
    { 
      headerName: 'Agent', 
      field: 'agent', 
      sortable: true, 
      filter: false, 
      cellRenderer: AgentLinkCell,
      headerClass: 'text-right',
      cellClass: 'text-right'
    },
    { 
      headerName: 'Total Calls', 
      field: 'totalCalls', 
      sortable: true, 
      filter: false,
      headerClass: 'text-right',
      cellClass: 'text-right'
    },
    { 
      headerName: 'Avg Professionalism', 
      field: 'averageProfessionalism', 
      sortable: true, 
      filter: false,
      cellRenderer: PerformanceCell,
      headerClass: 'text-center',
      cellClass: 'text-center'
    },
    { 
      headerName: 'Avg Sentiment', 
      field: 'averageSentiment', 
      sortable: true, 
      filter: false,
      cellRenderer: SentimentCell,
      headerClass: 'text-center',
      cellClass: 'text-center'
    },
    { 
      headerName: 'Avg Silence %', 
      field: 'averageSilencePercent', 
      sortable: true, 
      filter: false,
      valueFormatter: (params) => `${params.value.toFixed(2)}%`,
      headerClass: 'text-right',
      cellClass: 'text-right'
    },
    { 
      headerName: 'Avg Customer Satisfaction', 
      field: 'averageCustomerSatisfaction', 
      sortable: true, 
      filter: false,
      cellRenderer: PerformanceCell,
      headerClass: 'text-right',
      cellClass: 'text-right'
    },
    {
      headerName: 'Queues',
      field: 'callsByQueue',
      flex: 3,
      cellRenderer: (params) => {
          if (!params.value) return null;
          
          // Convert object to array of [key, value] pairs and sort by value (count) in descending order
          const sortedQueues = Object.entries(params.value)
              .sort((a, b) => b[1] - a[1]);
  
          return sortedQueues.map(([queueName, count], index) => (
              <Tippy 
                  key={index} 
                  content={
                      <div>
                          <div><span className="text-xs text-gray-400">Queue: </span>{queueName}</div>
                          <div><span className="text-xs text-gray-400">Call Count: </span>{count}</div>
                      </div>
                  }
              >
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded cursor-pointer">
                      {queueName} ({count})
                  </span>                        
              </Tippy>
          ));
      }
  }
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    console.log("GRID UPDATE", allAgentInfo)
    if (allAgentInfo && allAgentInfo.length > 0) {
      const sortedAgents = [...allAgentInfo].sort((a, b) => b.totalCalls - a.totalCalls);
      setRowData(sortedAgents);
    }
  }, [allAgentInfo]);

  const gridOptions = {
    pagination: true,
    paginationPageSize: 20,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  return (
    <div className="ag-theme-alpine" style={{ height: 1000, width: '100%' }}>
      <AgGridReact
        columnDefs={columns}
        rowData={rowData}
        onGridReady={onGridReady}
        gridOptions={gridOptions}
      />
    </div>
  );
};

export default AgentsGrid;